import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import {
  useCreateContactMutation,
  useUpdateContactMutation,
  useDeleteContactMutation,
  useMergeContactsMutation,
  CreateContactInput,
  UpdateContactInput,
  ContactPropertiesInput,
  GetContactDocument,
} from '@/lib/graphql/types-and-hooks';
import { buildBatchDeleteContactsMutation } from '@/lib/graphql/batch';
import { isLovablePreview } from '@/lib/utils/environment';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { toast } from 'sonner';
import { useCanMutate, assertCanMutate } from '@/lib/rbac';
import { mergeFailureMessage } from '@/lib/graphql/merge-refusal';

export interface CreateContactParams {
  primaryEmail: string;
  properties?: ContactPropertiesInput;
}

export interface UpdateContactParams {
  id: string;
  properties?: ContactPropertiesInput;
  customProperties?: Record<string, any>;
}

export const useContactMutations = () => {
  const { t } = useTranslation();
  const workspaceId = useWorkspaceId();
  const canMutate = useCanMutate();
  const client = useApolloClient();

  const [createContactMutation, { loading: creating }] = useCreateContactMutation();
  const [updateContactMutation, { loading: updating }] = useUpdateContactMutation();
  const [deleteContactMutation, { loading: deleting }] = useDeleteContactMutation();
  const [mergeContactsMutation, { loading: merging }] = useMergeContactsMutation();

  const createContact = useCallback(
    async (params: CreateContactParams) => {
      if (!assertCanMutate(canMutate)) return undefined;
      if (!workspaceId) {
        throw new Error('Workspace ID is required');
      }

      try {
        const input: CreateContactInput = {
          workspaceId,
          primaryEmail: params.primaryEmail,
          properties: params.properties || null,
        };

        const result = await createContactMutation({
          variables: { input },
          refetchQueries: ['getContacts'],
          awaitRefetchQueries: true,
        });

        if (result.data?.createContact?.contact) {
          toast.success(t('mutations.contactCreated'));
          return result.data.createContact.contact;
        }

        throw new Error(t('mutations.contactCreateFailed'));
      } catch (error) {
        console.error('Error creating contact:', error);
        toast.error(error instanceof Error ? error.message : t('mutations.contactCreateFailed'));
        throw error;
      }
    },
    [workspaceId, createContactMutation, canMutate, t]
  );

  const updateContact = useCallback(
    async (params: UpdateContactParams) => {
      if (!assertCanMutate(canMutate)) return undefined;
      try {
        const input: UpdateContactInput = {
          id: params.id, // Use contact ID as-is from GraphQL
          properties: params.properties || {}, // Use empty object instead of null to avoid backend NoneType errors
          customProperties: params.customProperties,
        };

        const result = await updateContactMutation({
          variables: { input },
          // No refetchQueries — the mutation returns full ContactFields which updates
          // the Apollo normalized cache. Inline edits use cellEdits local state for
          // immediate UI feedback. Refetching getContacts would reset pagination.
        });

        if (result.data?.updateContact?.contact) {
          toast.success(t('mutations.contactUpdatedSuccessfully'));
          return result.data.updateContact.contact;
        }

        throw new Error(t('mutations.contactUpdateFailed'));
      } catch (error) {
        console.error('Error updating contact:', error);
        toast.error(error instanceof Error ? error.message : t('mutations.contactUpdateFailed'));
        throw error;
      }
    },
    [updateContactMutation, canMutate, t]
  );

  const deleteContact = useCallback(
    async (id: string) => {
      if (!assertCanMutate(canMutate)) return false;
      try {
        const result = await deleteContactMutation({
          variables: { id }, // Use contact ID as-is from GraphQL
          refetchQueries: ['getContacts'],
          awaitRefetchQueries: true,
        });

        if (result.data?.deleteContact?.success) {
          toast.success(t('mutations.contactDeleted'));
          return true;
        }

        throw new Error(t('mutations.contactDeleteFailed'));
      } catch (error) {
        console.error('Error deleting contact:', error);
        toast.error(error instanceof Error ? error.message : t('mutations.contactDeleteFailed'));
        throw error;
      }
    },
    [deleteContactMutation, canMutate, t]
  );

  // Batch delete — single request with one aliased deleteContact per id, and a
  // single "N contacts deleted" toast (bulk delete previously fired one toast
  // per row). Mirrors deleteDeals in use-deal-mutations. No refetchQueries: the
  // windowed grid reconciles via removeRows and cached lists via the eviction.
  const deleteContacts = useCallback(
    async (ids: string[]) => {
      if (!assertCanMutate(canMutate)) return { success: false };
      if (ids.length === 0) return { success: true };
      if (isLovablePreview()) {
        return { success: true };
      }

      try {
        const result = await client.mutate({
          mutation: buildBatchDeleteContactsMutation(ids), // ids used as-is (Pattern 29, Section 4)
          update: (cache) => {
            // Evict deleted contacts so cached lists (detail sheets, account
            // contact lists) drop them reactively without a refetch.
            ids.forEach((id) => {
              cache.evict({ id: cache.identify({ __typename: 'Contact', id }) });
            });
            cache.gc();
          },
        });

        // Each alias returns { success }; a falsy success means the backend
        // rejected that delete even though the request itself didn't throw.
        const data = (result.data ?? {}) as Record<string, { success?: boolean } | null>;
        const failed = ids.filter((_, i) => !data[`delete_${i}`]?.success);
        if (failed.length > 0) {
          throw new Error(`Backend failed to delete ${failed.length} of ${ids.length} contacts`);
        }

        toast.success(t('mutations.contactsDeleted', { count: ids.length }));
        return { success: true };
      } catch (error) {
        console.error('Failed to delete contacts:', error);
        toast.error(t('mutations.contactsDeleteFailed'));
        return { success: false, error };
      }
    },
    [client, canMutate, t]
  );

  const mergeContacts = useCallback(
    async (primaryContactId: string, secondaryContactIds: string[]) => {
      if (!assertCanMutate(canMutate)) return undefined;
      if (!workspaceId) {
        throw new Error('Workspace ID is required');
      }

      let result: Awaited<ReturnType<typeof mergeContactsMutation>>;
      try {
        result = await mergeContactsMutation({
          variables: {
            args: {
              primaryContactId,
              otherContactIds: secondaryContactIds,
            },
          },
          // Evict secondaries from the normalized cache so they drop out of
          // contacts.edges and account.contacts.edges immediately. We can't rely
          // on the mutation's returned primaryContact for auto-normalization
          // because the backend returns it with a postgres-int id in DDB-mode
          // workspaces, which doesn't match the DDB-keyed cache entry the list
          // points at — see the refetch below.
          update: (cache, { data }) => {
            if (!data?.mergeContacts?.success) return;
            secondaryContactIds.forEach(id => {
              cache.evict({ id: cache.identify({ __typename: 'Contact', id }) });
            });
            cache.gc();
          },
        });
        if (!result.data?.mergeContacts?.success) throw new Error(t('mutations.contactsMergeFailed'));
      } catch (error) {
        console.error('Error merging contacts:', error);
        toast.error(mergeFailureMessage(error, t('mutations.contactsMergeFailed')));
        throw error;
      }

      // The merge has happened; a failed refresh must not report it as failed.
      try {
        // Refetch the merged primary by its DDB id through the singular
        // resolver. This doesn't go through OpenSearch (no consistency race)
        // and writes the merged data back into the DDB-keyed cache entry the
        // list row references, so the list updates without a full refetch.
        await client.query({
          query: GetContactDocument,
          variables: { id: primaryContactId },
          fetchPolicy: 'network-only',
        });
      } catch (error) {
        console.error('Contacts merged, but refreshing the primary failed:', error);
      }

      toast.success(t('mutations.contactsMerged', { contacts: secondaryContactIds.length }));
      return result.data?.mergeContacts?.primaryContact;
    },
    [workspaceId, mergeContactsMutation, client, canMutate, t]
  );

  return {
    createContact,
    updateContact,
    deleteContact,
    deleteContacts,
    mergeContacts,
    loading: creating || updating || deleting || merging,
    creating,
    updating,
    deleting,
    merging,
  };
};
